import {
	type UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import tokenProvider from "src/providers/token-provider";
import type { MerchantToken } from "src/types";
import { sleep } from "src/utils";

const requireMerchantId = (merchantId: string | null): string => {
	if (!merchantId) {
		throw new Error("Merchant ID is required");
	}
	return merchantId;
};

const tokenQueries = {
	listTokens: (): UseQueryResult<MerchantToken[]> => {
		const { merchantId } = useActiveMerchantId();

		return useQuery<MerchantToken[]>({
			queryKey: ["listTokens", merchantId],
			queryFn: () => tokenProvider.listTokens(requireMerchantId(merchantId)),
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	createToken: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (name: string) =>
				tokenProvider.createToken(requireMerchantId(merchantId), name),
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({ queryKey: ["listTokens", merchantId] });
			},
		});
	},

	deleteToken: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (tokenId: string) =>
				tokenProvider.deleteToken(requireMerchantId(merchantId), tokenId),
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({ queryKey: ["listTokens", merchantId] });
			},
		});
	},
};

export default tokenQueries;
